import React from "react";
import { BusinessEntity } from "../lib/types";

import { Box } from "../../../shared/components/ui/chakra";
import {
  Divider,
  Flex,
  Heading,
  HStack,
  Image,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react";

type BusinessEntitySummaryProps = Pick<
  BusinessEntity,
  | "name"
  | "name_kana"
  | "branch_name"
  | "prefecture"
  | "industry"
  | "number_of_employees"
  | "website_url"
  | "introduction"
  | "logo_image_url"
  | "business_type"
>;

const BusinessEntitySummary = ({
  name,
  name_kana,
  branch_name,
  prefecture,
  industry,
  number_of_employees,
  website_url,
  introduction,
  logo_image_url,
  business_type,
}: BusinessEntitySummaryProps) => {
  return (
    <Box
      borderRadius="lg"
      bg="white"
      px={{ base: 4, md: 9 }}
      pt={{ base: 4, md: 9 }}
      pb={{ base: 5, md: 7 }}
    >
      <Flex
        alignItems="flex-start"
        gap={{ base: 3, md: 8 }}
        w="full"
        flexDirection={{ base: "column", md: "row" }}
      >
        <Image
          flexShrink={0}
          flexGrow={0}
          boxSize={{ base: "4rem", md: "6.5rem" }}
          borderRadius="lg"
          fallback={
            <Box
              flexShrink={0}
              flexGrow={0}
              borderRadius="lg"
              boxSize={{ base: "4rem", md: "6.5rem" }}
              bg="gray.100"
            />
          }
          src={logo_image_url}
        />
        <Stack alignItems="flex-start" gap={{ base: 2, md: 4 }} mt={0.5}>
          <Box>
            <Box>
              <Heading
                size={{ base: "md", md: "lg" }}
                display="inline"
                verticalAlign="middle"
              >
                {name}
              </Heading>
              {branch_name && (
                <Heading
                  as="span"
                  size={{ base: "sm", md: "md" }}
                  color="textGray"
                  ml={0.5}
                  display="inline"
                  verticalAlign="middle"
                  fontWeight={"bold"}
                >
                  ({branch_name})
                </Heading>
              )}
            </Box>
            {name_kana && (
              <Box mt={1} fontSize="xs" color="textGray">
                {name_kana}
              </Box>
            )}
          </Box>
          <Stack gap={0.5} ml={1} fontSize={{ base: "xs", md: "sm" }}>
            <HStack
              gap={{ base: 2, md: 3 }}
              fontSize={{ base: "xs", md: "sm" }}
              flexWrap="wrap"
            >
              <Text>{business_type}</Text>
              <Divider orientation="vertical" h={4} borderColor="brand.300" />
              <Text>{prefecture.name}</Text>
              <Divider orientation="vertical" h={4} borderColor="brand.300" />
              <Text>{industry.name}</Text>
              <Divider orientation="vertical" h={4} borderColor="brand.300" />
              <Text>従業員数：{number_of_employees}名</Text>
            </HStack>
            {website_url !== "" && (
              <Link
                href={website_url}
                target="_blank"
                rel="noreferrer"
                color="textLink"
                textDecoration="underline"
              >
                {website_url}
              </Link>
            )}
          </Stack>
        </Stack>
      </Flex>
      <Box
        w="full"
        height="1px"
        bg="repeating-linear-gradient(to right, var(--chakra-colors-brand-300) 0px, var(--chakra-colors-brand-300) 4px, transparent 4px, transparent 8px)"
        mt={{ base: 5, md: 7 }}
        mb={{ base: 4, md: 8 }}
      />
      <Text whiteSpace="pre-wrap" fontSize={{ base: "sm", md: "md" }}>
        {introduction}
      </Text>
    </Box>
  );
};

export default BusinessEntitySummary;
